import React, { useEffect, useState, useRef } from 'react';
import {
  MdSmartphone, MdRssFeed, MdInstallMobile, MdKeyboard, MdSync, MdCheckCircle,
  MdInfo, MdContentCopy, MdRefresh, MdWarning,
} from 'react-icons/md';
import { useScanStream } from '../lib/useScanStream';
import { onTrigger, watchBattery, watchSignal } from '../lib/hardware';

const Section = ({ icon, title, children, color = 'var(--accent)' }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div className="card-header">
      <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {React.cloneElement(icon, { style: { color } })} {title}
      </div>
    </div>
    <div style={{ padding: '4px 16px 16px' }}>{children}</div>
  </div>
);

const Step = ({ n, title, children }) => (
  <div style={{ display: 'flex', gap: 14, padding: '12px 0', borderBottom: '1px solid #f0f2f5' }}>
    <div style={{
      flexShrink: 0, width: 28, height: 28, borderRadius: '50%',
      background: '#1a3c6e', color: 'white', fontWeight: 700, fontSize: 14,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{n}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 13, color: '#444', lineHeight: 1.6 }}>{children}</div>
    </div>
  </div>
);

const HandheldGuide = () => {
  // ── Live diagnostics — proves hardware features work on the current device ─
  const [battery, setBattery] = useState({ supported: false });
  const [signal, setSignal] = useState({ online: navigator.onLine });
  const [trigger, setTrigger] = useState({ count: 0, last: null });
  const [wedgeBuffer, setWedgeBuffer] = useState('');
  const [wedgeLast, setWedgeLast] = useState('');
  const wedgeRef = useRef(null);
  const { connected: wsConnected, lastEvent } = useScanStream('scans');

  useEffect(() => {
    let unsubBatt, unsubSig, unsubTrig;
    watchBattery(setBattery).then(u => { unsubBatt = u; });
    unsubSig = watchSignal(setSignal);
    unsubTrig = onTrigger((d) => {
      setTrigger(t => ({ count: t.count + 1, last: d }));
    });
    return () => { unsubBatt?.(); unsubSig?.(); unsubTrig?.(); };
  }, []);

  // Keep the wedge input focused while on this page so the user can test
  // the CX1500N's keyboard emulator typing into a focused field.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.activeElement?.tagName !== 'INPUT' &&
          document.activeElement?.tagName !== 'TEXTAREA') {
        wedgeRef.current?.focus();
      }
    }, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <div className="page-title"><MdSmartphone /> Handheld Connection Guide</div>
          <div className="page-subtitle">
            Set up a CX1500N (or any UHF Android handheld) to feed scans into this app.
          </div>
        </div>
      </div>

      {/* ── Live device diagnostics ─────────────────────────────────────────── */}
      <Section icon={<MdInfo />} title="Live Diagnostics — this device">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <Diag label="Browser" value={navigator.userAgent.split(' ').slice(-1)[0]} subtle title={navigator.userAgent} />
          <Diag label="Online"
            value={signal.online ? 'Yes' : 'No'}
            sub={signal.effectiveType ? `${signal.effectiveType.toUpperCase()} · ${signal.bars}/5 bars` : ''}
            ok={signal.online} />
          <Diag label="Battery API"
            value={battery.supported ? `${battery.level}%` : 'unsupported'}
            sub={battery.supported ? (battery.charging ? 'charging' : 'on battery') : 'use Android Chrome on the handheld'}
            ok={battery.supported} />
          <Diag label="WebSocket scan stream"
            value={wsConnected ? 'CONNECTED' : 'connecting…'}
            sub={lastEvent ? `Last: ${lastEvent.tag_number} (${lastEvent.rssi} dBm)` : 'Waiting for any scan'}
            ok={wsConnected} />
          <Diag label="Trigger (F1 / kc 292)"
            value={trigger.count > 0 ? `${trigger.count} fire(s)` : 'not yet pressed'}
            sub={trigger.last ? `code=${trigger.last.code} keyCode=${trigger.last.keyCode}` : 'pull the physical trigger'}
            ok={trigger.count > 0} />
          <Diag label="Last keyboard-wedge input"
            value={wedgeLast || '—'}
            sub={wedgeLast ? 'wedge is typing into the buffer' : 'point trigger at any tag'}
            ok={!!wedgeLast} />
        </div>

        {/* Hidden wedge buffer — keyboard-emulator types here */}
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 11, color: '#777', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Wedge test buffer (auto-focused)
          </label>
          <input
            ref={wedgeRef}
            value={wedgeBuffer}
            placeholder="Pull the trigger — the EPC should appear here"
            onChange={(e) => setWedgeBuffer(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (wedgeBuffer) setWedgeLast(wedgeBuffer);
                setWedgeBuffer('');
              }
            }}
            style={{
              width: '100%', padding: '10px 12px', marginTop: 4,
              border: '1.5px dashed #1565c0', borderRadius: 6,
              fontFamily: 'monospace', fontSize: 14, background: '#f8f9fb',
            }}
          />
          <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
            On a real CX1500N: keyboard emulator types the EPC + Enter.
            On this PC: type characters and press Enter to simulate.
          </div>
        </div>
      </Section>

      {/* ── How the CX1500N integrates ──────────────────────────────────────── */}
      <Section icon={<MdInstallMobile />} title="Setup — CX1500N (Android UHF handheld)">
        <Step n={1} title="Install the UHF Keyboard helper apk">
          On the CX1500N, install <code>KeyBoardHelper_2.3.31.apk</code> or the
          <code> IdentiumKeyboardEmulator</code> app from <code>Reference_Material/CX1500N_New SDK and Documentation/</code>.
          Use <code>adb install</code> from a dev machine, or copy the APK to the device and tap to install.
        </Step>
        <Step n={2} title="Enable the accessibility service">
          Open the UHF Keyboard helper app → tap <strong>Open Accessibility Settings</strong> →
          enable <strong>Identium UHF Keyboard Emulator</strong> (this is the literal accessibility-service name shown by Android — leave it alone). Without this, Android won't deliver
          the trigger key event to the system input.
        </Step>
        <Step n={3} title="Toggle the wedge ON">
          Inside the keyboard app, switch <strong>Enable Keyboard Mode</strong> to ON. The wedge runs in
          the background regardless of which app is foregrounded.
        </Step>
        <Step n={4} title="Open the web app in Chrome and sign in">
          On the CX1500N, open <code>http://&lt;your-server&gt;:3006/</code> (or this dev server's URL)
          in Chrome and sign in. From the Chrome menu, choose <strong>Add to Home screen</strong> to
          install it as a PWA — it then opens in standalone mode like a native app.
        </Step>
        <Step n={5} title="Verify with Live Diagnostics above">
          On this guide page (with the wedge buffer focused), pull the physical trigger. The EPC should
          type into the buffer and you'll see "Last keyboard-wedge input" go green. The hardware F1
          trigger event should also be picked up by our global <code>identium-trigger</code> listener.
        </Step>
      </Section>

      {/* ── How real-time scan stream works ─────────────────────────────────── */}
      <Section icon={<MdSync />} title="Real-time scan stream (WebSocket)">
        <p style={{ marginTop: 0, fontSize: 13, color: '#444', lineHeight: 1.6 }}>
          Any source that POSTs to <code>/api/raw-inventory/scan</code> with a JSON
          body of <code>{'{tag_number, rssi, session_id?}'}</code> is broadcast over the
          WebSocket bus to all connected clients. Asset Search subscribes to
          <code> asset:&lt;rfid_tag&gt;</code> for live proximity; this guide page subscribes
          to the global <code>scans</code> topic so you can verify scans are flowing.
        </p>
        <div style={{
          background: '#f8f9fb', border: '1px solid #e0e4eb', borderRadius: 6,
          padding: 12, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5,
        }}>
{`POST /api/raw-inventory/scan
Headers: Authorization: Bearer <JWT>
Body:    { "tag_number": "E200-...", "rssi": -52, "session_id": "uuid-or-null" }`}
        </div>
      </Section>

      {/* ── Trigger handler ─────────────────────────────────────────────────── */}
      <Section icon={<MdKeyboard />} title="Hardware trigger (F1 / keyCode 292)">
        <p style={{ marginTop: 0, fontSize: 13, color: '#444', lineHeight: 1.6 }}>
          The CX1500N's trigger fires <code>KeyboardEvent.code = "F1"</code>
          (and on some firmwares, vendor <code>keyCode 292</code>). A global listener in the app
          dispatches a custom <code>identium-trigger</code> event that any page can subscribe to.
          The wedge fires the EPC after a short delay — pages don't normally need to handle
          the trigger directly, but you can if you want a "trigger pulled" indicator.
        </p>
        <div style={{
          background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 6,
          padding: 10, fontSize: 12, color: '#7a5400', marginTop: 8,
        }}>
          <MdWarning /> The browser also uses F1 for "Help". The app calls
          <code> preventDefault()</code> so it won't open the help panel.
        </div>
      </Section>

      <Section icon={<MdRssFeed />} title="Pushing scans from a fixed reader">
        <p style={{ marginTop: 0, fontSize: 13, color: '#444', lineHeight: 1.6 }}>
          The Reader Manager page already supports Impinj Octane REST-API readers: configure
          the IP, credentials and antenna power, then hit <strong>Connect</strong> + <strong>Start</strong>.
          The reader's tag-read buffer is polled by the backend, and each new tag is forwarded to
          <code> /raw-inventory/scan</code> with the RSSI value, where it joins the same scan stream
          handhelds use. Asset Search and the proximity finder don't care which source the scan came from.
        </p>
      </Section>
    </div>
  );
};

const Diag = ({ label, value, sub, ok, subtle, title }) => (
  <div title={title} style={{
    padding: 12, borderRadius: 8, background: '#f8f9fb',
    borderLeft: `3px solid ${ok === true ? '#2e7d32' : ok === false ? '#c62828' : '#90a4ae'}`,
  }}>
    <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
      {label}
    </div>
    <div style={{ fontSize: subtle ? 12 : 16, fontWeight: subtle ? 400 : 700, marginTop: 4,
      color: ok === true ? '#2e7d32' : ok === false ? '#c62828' : '#1a3c6e',
      wordBreak: 'break-word',
    }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: '#777', marginTop: 2 }}>{sub}</div>}
  </div>
);

export default HandheldGuide;
